import type { EnemyType } from '../balance';
import { VERDANT_ABILITIES } from '../arenaAbilities';
import { VERDANT_PASSIVE_KEYS } from '../passives';
import type { LevelDefinition } from './types';

const ENEMIES: Readonly<Record<string, EnemyType>> = {
  yeastling: {
    key: 'yeastling', hp: 18, contactDmg: 5, speedPerSec: 48, radius: 10, xp: 1,
    color: 0xb9e84f, kind: 'chase', sprite: 'yeastling',
  },
  glassGnat: {
    key: 'glassGnat', hp: 11, contactDmg: 4, speedPerSec: 92, radius: 7, xp: 1,
    color: 0xa6f2dc, kind: 'weave', sprite: 'glassGnat',
  },
  rootling: {
    key: 'rootling', hp: 78, contactDmg: 9, speedPerSec: 0, radius: 12, xp: 3,
    color: 0x5d9d4f, kind: 'stationary', sprite: 'rootling',
    bloomEmitter: { cooldownSec: 5.2, wallCount: 1 }, onDeath: { dropsNectar: true },
  },
  kegCrab: {
    key: 'kegCrab', hp: 210, contactDmg: 15, speedPerSec: 54, radius: 16, xp: 7,
    color: 0xc8873f, kind: 'weave', sprite: 'kegCrab',
  },
  mycelialSlinger: {
    key: 'mycelialSlinger', hp: 82, contactDmg: 10, speedPerSec: 38, radius: 10, xp: 5,
    color: 0xd680d8, kind: 'ranged', sprite: 'mycelialSlinger',
    rangedAttack: { rangePx: 500, sprite: 'sporeShot', color: 0xd680d8, damage: 10, speed: 150, radius: 6, lifeSec: 4.4, bounces: 2 },
  },
  copperWasp: {
    key: 'copperWasp', hp: 270, contactDmg: 20, speedPerSec: 68, radius: 12, xp: 14,
    color: 0xffc35a, kind: 'charge', sprite: 'copperWasp',
  },
  bloomBrute: {
    key: 'bloomBrute', hp: 510, contactDmg: 24, speedPerSec: 39, radius: 20, xp: 24,
    color: 0x80c965, kind: 'chase', sprite: 'bloomBrute', onDeath: { bloomWallCount: 3 },
  },
};

const BOSSES: Readonly<Record<string, EnemyType>> = {
  vatmother: {
    key: 'vatmother', hp: 2750, contactDmg: 27, speedPerSec: 28, radius: 36, xp: 140,
    color: 0x79b84f, kind: 'summon', sprite: 'vatmother',
    summon: { enemyKey: 'yeastling', count: 4, cooldownSec: 3.2 }, bloomEmitter: { cooldownSec: 7, wallCount: 2 },
  },
  copperhorn: {
    key: 'copperhorn', hp: 5200, contactDmg: 38, speedPerSec: 100, radius: 31, xp: 230,
    color: 0xffbc55, kind: 'charge', sprite: 'copperhorn', onDeath: { bloomWallCount: 2 },
  },
  verdantAlembic: {
    key: 'verdantAlembic', hp: 11200, contactDmg: 62, speedPerSec: 82, radius: 32, xp: 340,
    color: 0xf35fca, kind: 'ranged', sprite: 'verdantAlembic',
    rangedAttack: { rangePx: 560, sprite: 'sporeShot', color: 0xf35fca, damage: 15, speed: 180, radius: 7, lifeSec: 4.6, bounces: 3 },
    bloomEmitter: { cooldownSec: 5.5, wallCount: 3 },
  },
};

/** Approved Expansion Pack level one. It keeps the same player, combat loop, and run
 * duration as Base Lab while replacing the roster, terrain language, and spatial pressure. */
export const VERDANT_STILL_LEVEL: LevelDefinition = {
  id: 'expansion_1',
  displayName: 'The Verdant Still',
  entitlement: 'expansion',
  runLengthSec: 900,
  musicTrack: 'verdantStill',
  victoryBossKey: 'verdantAlembic',
  schedule: [
    { t: 120, kind: 'event', banner: 'FERMENTATION SURGE', burst: 30 },
    { t: 150, kind: 'bloom', banner: 'BLOOM PULSE', bloomWallCount: 2 },
    { t: 300, kind: 'boss', bossKey: 'vatmother', banner: 'THE VATMOTHER' },
    { t: 480, kind: 'bloom', banner: 'ROOTS AWAKEN', bloomWallCount: 4 },
    { t: 600, kind: 'boss', bossKey: 'copperhorn', banner: 'COPPERHORN STAG' },
    { t: 720, kind: 'bloom', banner: 'THE GREENHOUSE CLOSES', bloomWallCount: 5 },
    { t: 900, kind: 'boss', bossKey: 'verdantAlembic', banner: 'THE VERDANT ALEMBIC' },
  ],
  tierUnlocks: [
    { t: 0, key: 'yeastling', w: 10 },
    { t: 0, key: 'glassGnat', w: 6 },
    { t: 90, key: 'rootling', w: 4 },
    { t: 210, key: 'kegCrab', w: 4 },
    { t: 330, key: 'mycelialSlinger', w: 3 },
    { t: 360, key: 'copperWasp', w: 2 },
    { t: 450, key: 'bloomBrute', w: 1 },
  ],
  enemyTypes: ENEMIES,
  bossTypes: BOSSES,
  weaponKeys: [
    'sapFlask', 'cinderHops', 'thornNeedle', 'fireflyJar', 'sporeShroud', 'seedHalo',
    'boilingWort', 'thunderSap', 'smolderMold', 'thornCrown', 'fireflyHalo', 'nectarCatalyst',
  ],
  startingWeaponKey: 'sapFlask',
  passiveKeys: VERDANT_PASSIVE_KEYS,
  abilities: VERDANT_ABILITIES,
  spawnTuning: {
    distancePx: 720, distanceJitterPx: 140, maxActive: 600, maxPerTick: 10,
    baseSpawnPerSec: 2.6, maxSpawnPerSec: 31, rampToMaxSec: 480, hpScalePerMin: 0.1, dmgScalePerMin: 0.06,
  },
  visualTheme: {
    backgroundColor: 0x0d2426, gridColor: 0x174044, groundTexture: 'verdant',
    obstacleTexture: 'copperStill', puddleTexturePrefix: 'irrigation', foliage: 'roots', altarTexture: 'verdantAltar',
  },
  bloomVines: {
    maxActiveWalls: 6, wallRadius: 22, telegraphSec: 0.7, durationSec: 4.8,
    minDistancePx: 110, maxDistancePx: 220,
  },
};
